// Ruevale — live parts of the product page messages (snippets/rv-cv.liquid):
// <rv-cv-live> refreshes a message when the variant changes,
// <rv-cv-delivery> recalculates delivery dates for today,
// <rv-cv-cutoff> counts down to the daily order cutoff,
// <rv-free-shipping> shows what is left in the cart before free shipping.
// Times use the store's timezone (data-timezone, default America/New_York), not the visitor's clock.
// Dates, times and amounts follow the storefront language (<html lang>), US English by default.

import { StandardEvents } from '@shopify/events';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const pad = (n) => String(n).padStart(2, '0');

/** Today's date and the current time in a timezone. */
function zonedNow(timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

const toUtc = (date) => new Date(Date.UTC(date.year, date.month - 1, date.day));
const addDays = (date, count) => {
  const next = toUtc(date);
  next.setUTCDate(next.getUTCDate() + count);
  return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() };
};
const weekdayKey = (date) => DAY_KEYS[toUtc(date).getUTCDay()];
const isoDate = (date) => `${date.year}-${pad(date.month)}-${pad(date.day)}`;
const locale = () => document.documentElement.lang || 'en-US';

/** A date in the storefront language: 'month_day' (Oct 6), 'short' (Tue, Oct 6), 'long' (Tuesday, October 6), 'day' (6), 'weekday' (Tuesday). */
const DATE_FORMATS = {
  month_day: { month: 'short', day: 'numeric' },
  day_month: { month: 'short', day: 'numeric' },
  short: { weekday: 'short', month: 'short', day: 'numeric' },
  long: { weekday: 'long', month: 'long', day: 'numeric' },
  day: { day: 'numeric' },
  weekday: { weekday: 'long' },
};
function formatDate(date, format) {
  const options = DATE_FORMATS[format] || DATE_FORMATS.short;
  return new Intl.DateTimeFormat(locale(), { timeZone: 'UTC', ...options }).format(toUtc(date));
}

/** A time of day on the storefront clock: 2 PM, 2:30 PM. */
function formatTime(hour, minute) {
  const options = minute ? { hour: 'numeric', minute: '2-digit' } : { hour: 'numeric' };
  return new Intl.DateTimeFormat(locale(), { timeZone: 'UTC', ...options }).format(new Date(Date.UTC(2000, 0, 1, hour, minute)));
}

/**
 * Writes a message template into an element: each [placeholder] gets its value from `values`
 * (a string, or a function called once per occurrence) inside a highlighted span.rv-cv__em.
 */
function fillText(element, template, values) {
  const parts = [];
  let last = 0;
  template.replace(/\[([a-z_]+)\]/g, (match, key, index) => {
    if (!(key in values)) return match;
    parts.push(document.createTextNode(template.slice(last, index)));
    const value = typeof values[key] === 'function' ? values[key]() : values[key];
    const span = document.createElement('span');
    span.className = 'rv-cv__em';
    span.textContent = value;
    parts.push(span);
    last = index + match.length;
    return match;
  });
  parts.push(document.createTextNode(template.slice(last)));
  element.replaceChildren(...parts);
}

/* ---------- Refresh a message when the variant changes ---------- */
class RvCvLive extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  onProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`rv-cv-live[data-block-id="${this.dataset.blockId}"]`);
        if (fresh) this.replaceWith(document.importNode(fresh, true));
      })
      .catch(() => {});
  };
}

/* ---------- Keep a message on one line ---------- */
// Steps the text size down (to 12px at least) until the message fits its line; only then is it cut with …
class RvCvFit extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('.rv-cv__text');
    if (!this.text) return;
    this.observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (width === this.width) return;
      this.width = width;
      this.fit();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  fit() {
    const text = this.text;
    text.style.fontSize = '';
    const available = text.getBoundingClientRect().width;
    // Width the text needs on one line, measured to the sub-pixel
    const needed = () => {
      text.style.width = 'max-content';
      const width = text.getBoundingClientRect().width;
      text.style.width = '';
      return width;
    };
    let size = parseFloat(getComputedStyle(text).fontSize);
    while (needed() > available && size > 12) {
      size -= 0.25;
      text.style.fontSize = `${size}px`;
    }
  }
}

/* ---------- Delivery dates ---------- */
class RvCvDelivery extends HTMLElement {
  connectedCallback() {
    const text = this.querySelector('[data-rv-cv-text]');
    if (!text || !this.dataset.text) return;

    const today = zonedNow(this.dataset.timezone || 'America/New_York');
    const counted = { calendar: null, weekdays: ['sat', 'sun'], mon_sat: ['sun'] }[this.dataset.count] || null;
    const later = (days) => {
      let date = today;
      for (let added = 0; added < days; ) {
        date = addDays(date, 1);
        if (!counted || !counted.includes(weekdayKey(date))) added++;
      }
      return date;
    };

    const first = later(Number(this.dataset.min || 0));
    const last = later(Number(this.dataset.max || 0));
    // Same month: "Oct 6–13" instead of "Oct 6–Oct 13"
    const sameMonth = first.year === last.year && first.month === last.month;
    const lastFormat = this.dataset.format === 'month_day' && sameMonth ? 'day' : this.dataset.format;
    const dates = [formatDate(first, this.dataset.format), formatDate(last, lastFormat)];
    let next = 0;
    fillText(text, this.dataset.text, { date: () => dates[Math.min(next++, 1)] });
  }
}

/* ---------- Order cutoff ---------- */
class RvCvCutoff extends HTMLElement {
  connectedCallback() {
    this.timeZone = this.dataset.timezone || 'America/New_York';
    this.days = (this.dataset.days || '').split(',').filter(Boolean);
    this.holidays = new Set((this.dataset.holidays || '').split(',').map((d) => d.trim()).filter(Boolean));
    this.update();
    this.timer = setInterval(() => this.update(), 20000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  isWorkingDay(date) {
    return this.days.includes(weekdayKey(date)) && !this.holidays.has(isoDate(date));
  }

  update() {
    const text = this.querySelector('[data-rv-cv-text]');
    if (!text) return;

    const now = zonedNow(this.timeZone);
    const [cutHour, cutMinute] = (this.dataset.cutoff || '14:00').split(':').map(Number);
    const left = cutHour * 60 + (cutMinute || 0) - (now.hour * 60 + now.minute);
    const hour = formatTime(cutHour, cutMinute || 0);

    if (this.isWorkingDay(now) && left > 0) {
      const hours = Math.floor(left / 60);
      const minutes = left % 60;
      const time =
        hours > 0
          ? (this.dataset.hoursMinutes || '[h] hr [m] min').replace('[h]', hours).replace('[m]', minutes)
          : (this.dataset.minutes || '[m] min').replace('[m]', minutes);
      fillText(text, this.dataset.textBefore, { time, cutoff: hour });
      this.hidden = false;
      return;
    }

    if (!this.dataset.textAfter) {
      this.hidden = true;
      return;
    }

    let shipDay = addDays(now, 1);
    for (let i = 0; i < 30 && !this.isWorkingDay(shipDay); i++) shipDay = addDays(shipDay, 1);
    const label =
      isoDate(shipDay) === isoDate(addDays(now, 1)) ? this.dataset.tomorrow || 'tomorrow' : formatDate(shipDay, 'weekday');
    fillText(text, this.dataset.textAfter, { day: label, cutoff: hour });
    this.hidden = false;
  }
}

/* ---------- Free shipping ---------- */
class RvFreeShipping extends HTMLElement {
  connectedCallback() {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    this.threshold = Math.round(Number(this.dataset.threshold || 0) * 100 * rate);
    this.currency = window.Shopify?.currency?.active || this.dataset.currency || 'USD';
    this.render(Number(this.dataset.cartTotal || 0));
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  onCartUpdate = async (event) => {
    try {
      await event.promise;
    } catch (error) {
      // The add failed; the cart below is still the source of truth.
    }
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`).then((response) => response.json());
    this.render(cart.total_price);
  };

  render(total) {
    const text = this.querySelector('[data-rv-cv-text]');
    if (!this.threshold || !text) return;
    const remaining = Math.max(this.threshold - total, 0);
    // $49.95, and $20 for whole amounts
    const whole = remaining % 100 === 0;
    const money = new Intl.NumberFormat(locale(), {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(remaining / 100);
    fillText(text, remaining > 0 ? this.dataset.textRemaining : this.dataset.textReached, { amount: money });
    this.querySelector('.rv-cv__bar-fill')?.style.setProperty('--rv-cv-fill', `${Math.min(total / this.threshold, 1) * 100}%`);
    this.toggleAttribute('data-reached', remaining === 0);
    this.hidden = false;
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('rv-cv-live', RvCvLive);
define('rv-cv-fit', RvCvFit);
define('rv-cv-delivery', RvCvDelivery);
define('rv-cv-cutoff', RvCvCutoff);
define('rv-free-shipping', RvFreeShipping);
